import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Receipt, BookOpen, AlertTriangle, ArrowRight, Package, TrendingUp, KeyRound, X, Wallet } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function PortalOverview() {
  const ctx = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [bills, setBills] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [o, b, a, p] = await Promise.all([
          axios.get(`${API}/portal/orders`, { withCredentials: true }),
          axios.get(`${API}/portal/bills`, { withCredentials: true }),
          axios.get(`${API}/portal/order-history-analytics`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/portal/recent-payments`, { withCredentials: true }).catch(() => ({ data: { payments: [] } })),
        ]);
        setOrders(o.data || []);
        setBills(b.data);
        setAnalytics(a.data);
        setRecentPayments(p.data?.payments || []);
      } catch {}
    })();
  }, []);

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwBusy(true);
    try {
      await axios.post(`${API}/portal/change-password`, { old_password: pwForm.old_password, new_password: pwForm.new_password }, { withCredentials: true });
      toast.success('Password changed successfully');
      setPwOpen(false);
      setPwForm({ old_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change password'); }
    finally { setPwBusy(false); }
  };

  const openOrders = orders.filter(o => ['Not Accepted', 'Accepted', 'In Production', 'Packing', 'Pending Acceptance'].includes(o.status)).length;
  const overdueCount = (bills?.bills || []).filter(b => b.is_overdue).length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-zinc-50">Welcome back{ctx?.me?.client?.name ? `, ${ctx.me.client.name}` : ''}</h1>
        <p className="text-zinc-500 text-sm mt-1">Your orders, bills and account — all in one place.</p>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/portal/orders" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/40 transition-colors group" data-testid="tile-open-orders">
          <div className="flex items-center justify-between">
            <ShoppingCart size={18} className="text-amber-400" />
            <ArrowRight size={13} className="text-zinc-600 group-hover:text-amber-400" />
          </div>
          <div className="font-mono font-bold text-3xl text-zinc-50 mt-2">{openOrders}</div>
          <div className="text-zinc-400 text-xs mt-1">Open orders</div>
        </Link>
        <Link to="/portal/bills" className={`bg-zinc-900 border rounded-xl p-5 transition-colors group ${overdueCount ? 'border-red-700/50 hover:border-red-500/70' : 'border-zinc-800 hover:border-amber-500/40'}`} data-testid="tile-outstanding">
          <div className="flex items-center justify-between">
            <Receipt size={18} className={overdueCount ? 'text-red-400' : 'text-amber-400'} />
            <ArrowRight size={13} className="text-zinc-600 group-hover:text-amber-400" />
          </div>
          <div className={`font-mono font-bold text-2xl mt-2 ${overdueCount ? 'text-red-400' : 'text-zinc-50'}`}>{fmt(bills?.total_outstanding || 0)}</div>
          <div className="text-zinc-400 text-xs mt-1">
            Outstanding{overdueCount ? ` · ${overdueCount} overdue` : ''}
          </div>
        </Link>
        <Link to="/portal/place-order" className="bg-gradient-to-br from-amber-500/15 to-zinc-900 border border-amber-500/40 rounded-xl p-5 hover:border-amber-500/70 transition-colors group" data-testid="tile-place-order">
          <div className="flex items-center justify-between">
            <Package size={18} className="text-amber-400" />
            <ArrowRight size={13} className="text-amber-400 group-hover:translate-x-0.5 transition-transform" />
          </div>
          <div className="font-heading font-bold text-xl text-zinc-50 mt-3">Place a New Order</div>
          <div className="text-zinc-400 text-xs mt-1">We'll confirm within 24 hrs</div>
        </Link>
      </div>

      {overdueCount > 0 && (
        <div className="bg-red-900/20 border border-red-800/40 rounded-xl p-4 flex items-start gap-3" data-testid="overdue-banner">
          <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-red-300 text-sm font-semibold">You have {overdueCount} overdue bill(s)</div>
            <div className="text-zinc-400 text-xs mt-0.5">Please settle at the earliest. Tap Bills to view details.</div>
          </div>
        </div>
      )}

      {/* iter162 — Recent payments feed: shows the buyer that their
          lumpsum payment was actually allocated against specific bills. */}
      {recentPayments.length > 0 && (
        <div className="bg-zinc-900 border border-green-500/30 rounded-xl overflow-hidden" data-testid="portal-recent-payments">
          <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
            <Wallet size={14} className="text-green-400" />
            <h3 className="font-heading font-semibold text-zinc-50 text-sm">Your Recent Payments</h3>
            <span className="text-zinc-500 text-[10px] ml-auto">Last 90 days</span>
          </div>
          <div>
            {recentPayments.slice(0, 8).map((p, i) => (
              <div key={p.payment_id || i}
                data-testid={`portal-payment-${i}`}
                className="px-5 py-3 border-b border-zinc-800/50 last:border-0 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-green-400 font-mono font-semibold">{fmt(p.amount)}</span>
                    <span className="text-zinc-600 text-[11px]">via {p.method || '—'}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      p.invoice_status === 'Paid' ? 'bg-green-500/20 text-green-400' :
                      p.invoice_status === 'Partial' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>{p.invoice_status === 'Partial' ? 'Partially Paid' : p.invoice_status}</span>
                  </div>
                  <div className="text-zinc-500 text-[11px] mt-0.5 truncate">
                    Allocated to bill <span className="text-amber-400 font-mono">{p.invoice_number}</span>
                    {p.invoice_date && <span className="text-zinc-600"> (dt {p.invoice_date})</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-zinc-500 text-[10px]">{p.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent orders preview */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-zinc-50 text-sm">Recent Orders</h3>
          <Link to="/portal/orders" className="text-xs text-amber-400 hover:text-amber-300">View all →</Link>
        </div>
        <div>
          {orders.slice(0, 6).map(o => (
            <div key={o.id} className="px-5 py-3 border-b border-zinc-800/50 last:border-0 flex items-center justify-between">
              <div>
                <div className="text-zinc-50 text-sm font-medium">{o.invoice_number || o.voucher_no || o.id.slice(0, 10)}</div>
                <div className="text-zinc-500 text-[11px]">{o.created_at?.slice(0, 10)} · {(o.items || [])[0]?.product_name || '—'}</div>
              </div>
              <div className="text-right">
                <div className="text-amber-400 font-mono text-sm font-semibold">{fmt(o.total_amount)}</div>
                <div className="text-zinc-500 text-[10px]">{o.status}</div>
              </div>
            </div>
          ))}
          {orders.length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">No orders yet — place your first!</div>}
        </div>
      </div>

      {/* ── Your Ordering Pattern ── */}
      {analytics && analytics.total_orders > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="portal-analytics">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
              <TrendingUp size={14} className="text-amber-400" /> Your Ordering Pattern
            </h3>
            <div className="text-zinc-500 text-xs">
              {analytics.total_orders} orders · ₹{analytics.total_spend.toLocaleString('en-IN')} lifetime
            </div>
          </div>
          {/* Monthly spend bars */}
          {analytics.monthly_spend.length > 0 && (
            <div className="mb-4">
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Monthly Spend (last 12 months)</div>
              <div className="flex items-end gap-1.5 h-20">
                {analytics.monthly_spend.map(m => {
                  const max = Math.max(...analytics.monthly_spend.map(x => x.amount));
                  const h = max > 0 ? (m.amount / max) * 100 : 0;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center" title={`${m.month}: ₹${m.amount.toLocaleString('en-IN')}`}>
                      <div className="w-full bg-amber-500/70 rounded-t" style={{ height: `${h}%` }} />
                      <div className="text-[9px] text-zinc-600 mt-1 font-mono">{m.month.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Top products + next due */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Your Top Products</div>
              <div className="space-y-1.5">
                {analytics.top_products.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-zinc-950 rounded-lg px-3 py-1.5 border border-zinc-800">
                    <span className="text-zinc-200 truncate">{p.product_name}</span>
                    <span className="text-zinc-500 font-mono">{p.qty.toFixed(1)} {p.unit || ''} · {p.orders}×</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2">Re-order Suggestions</div>
              <div className="space-y-1.5">
                {Object.entries(analytics.product_cadence || {}).slice(0, 5).map(([pn, c]) => {
                  const next = c.next_due_estimate ? new Date(c.next_due_estimate) : null;
                  const now = new Date();
                  const overdue = next && next < now;
                  return (
                    <div key={pn} className={`flex items-center justify-between text-xs rounded-lg px-3 py-1.5 border ${overdue ? 'bg-red-500/10 border-red-500/40' : 'bg-zinc-950 border-zinc-800'}`}>
                      <div className="truncate">
                        <div className="text-zinc-200 truncate">{pn}</div>
                        <div className="text-zinc-500 text-[10px] font-mono">every ~{c.avg_days_between_orders}d</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-[10px] font-semibold ${overdue ? 'text-red-300' : 'text-amber-300'}`}>
                          {overdue ? 'Time to reorder!' : `in ${Math.round((next - now) / (1000 * 60 * 60 * 24))}d`}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {Object.keys(analytics.product_cadence || {}).length === 0 && (
                  <div className="text-zinc-600 text-xs italic">Not enough history yet — order 2+ times for reorder predictions.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password CTA */}
      <div className="flex justify-end">
        <button onClick={() => setPwOpen(true)} data-testid="portal-change-pw-btn"
          className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-amber-300">
          <KeyRound size={12} /> Change Password
        </button>
      </div>

      {pwOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPwOpen(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="portal-pw-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">Change Password</h3>
              <button onClick={() => setPwOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Current Password</label>
                <input type="password" value={pwForm.old_password} onChange={e => setPwForm(f => ({ ...f, old_password: e.target.value }))}
                  data-testid="pw-current" autoFocus
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">New Password</label>
                <input type="password" value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
                  data-testid="pw-new"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Confirm New Password</label>
                <input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                  data-testid="pw-confirm"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setPwOpen(false)} className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-xs">Cancel</button>
                <button onClick={changePassword} disabled={pwBusy} data-testid="pw-save"
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black rounded-lg text-xs font-semibold">
                  {pwBusy ? 'Saving…' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
